// js/fireworks-shared/fireworks-lib-2.js
// Shared fireworks particle engine for both Desktop and VR versions.
// Based on desktop_scripts/fireworks/fireworks-lib.js with VR extras merged in.

let sceneEl = null;
const THREE = AFRAME.THREE;

let SETTINGS = {};
let sparkleTexture, fireballTexture;
let isEnabled = true;

let _cachedZeroPlane = null;
const _activeTimeoutIds = new Set();
let _activeFireworkCount = 0;
let _autoLaunchIntervalId = null;
let _rocketMaterialTemplate = null;
let _explosionMaterialTemplate = null;
const _activeRockets = new Set();
const _activeExplosions = new Set();
let _tickScheduled = false;

function _scheduleTick() {
  if (_tickScheduled) return;
  const hasWork = _activeRockets.size > 0 || _activeExplosions.size > 0;
  if (!hasWork) return;
  _tickScheduled = true;

  if (!_isVRMode()) {
    window.requestAnimationFrame(() => _tick());
  }
}

function _onSceneTick() {
  const hasWork = _activeRockets.size > 0 || _activeExplosions.size > 0;
  if (!hasWork) {
    _tickScheduled = false;
    return;
  }
  _tick();
  _tickScheduled = true;
}

function _tick() {
  _tickScheduled = false;
  if (!isEnabled) return;

  const delta = SETTINGS.ANIMATION.DELTA_TIME || 0.016;

  for (const rocket of _activeRockets) {
    rocket.step(delta);
  }

  for (const explosion of _activeExplosions) {
    explosion.step(delta);
  }

  if (!_isVRMode()) {
    _scheduleTick();
  }
}

function _getZeroPlane() {
  if (!_cachedZeroPlane) {
    _cachedZeroPlane = document.querySelector('#stock-3d-cube');
  }
  return _cachedZeroPlane;
}

function _trackTimeout(fn, delay) {
  const id = setTimeout(() => {
    _activeTimeoutIds.delete(id);
    fn();
  }, delay);
  _activeTimeoutIds.add(id);
  return id;
}

function _isVRMode() {
  if (typeof window !== 'undefined' && window.FIREWORKS_ASSET_MODE) {
    return window.FIREWORKS_ASSET_MODE === 'vr';
  }
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return /\/vr[^/]*(\.html)?(\?|$)/.test(path);
}

function _getXRSession() {
  if (!sceneEl) return false;
  const session = sceneEl.renderer && sceneEl.renderer.xr && sceneEl.renderer.xr.getSession
    ? sceneEl.renderer.xr.getSession()
    : null;
  return session;
}

function _getAssetBasePath() {
  return _isVRMode() ? '/vr-assets/particles' : '/desktop_assets/fireworks';
}

function _readFireworkOnSetting() {
  if (typeof getCacheItem === 'function') {
    const cfg = getCacheItem('configureControlSettings');
    if (cfg && typeof cfg.isFireworkOn === 'boolean') return cfg.isFireworkOn;
  }
  try {
    const raw = localStorage.getItem('configureControlSettings');
    if (raw) {
      const cfg = JSON.parse(raw);
      const val = cfg?.value?.isFireworkOn ?? cfg?.isFireworkOn;
      if (typeof val === 'boolean') return val;
    }
  } catch (e) {}
  return null;
}

function getDefaultSettings() {
  return {
    ENABLED: true,
    ROCKET: {
      VELOCITY: { min: 0.2, max: 0.4 },
      HEIGHT: { min: 0.1, max: 0.3 },
      SIZE: 0.03,
      LAUNCH_AREA: {
        x: { min: -0.3, max: 0.3 },
        z: { min: -0.3, max: 0.3 }
      }
    },
    EXPLOSION: {
      PARTICLE_COUNT: 25,
      PARTICLE_SIZE: 0.1,
      LIFETIME: 0.8,
      VELOCITY: { min: 0.3, max: 0.5 },
      COLOR_TRANSITION: { midpoint: 0.3 }
    },
    PHYSICS: { GRAVITY: -0.5 },
    ANIMATION: { DELTA_TIME: 0.016 },
    COLORS: {
      PALETTE: ['#ff3333', '#ffff66', '#66ff66', '#66ffff', '#6666ff', '#ff66ff']
    },
    TEXTURES: {
      SPARKLE: `${_getAssetBasePath()}/sparkle.png`,
      FIREBALL: `${_getAssetBasePath()}/fireball.png`
    },
    AUTO_LAUNCH: {
      ENABLED: false,
      INTERVAL: 5000,
      MIN_GROUP_SIZE: 2,
      MAX_GROUP_SIZE: 4
    },
    PERFORMANCE: {
      MAX_CONCURRENT_FIREWORKS: 50
    }
  };
}

function loadSettings() {
  fetch('/js/fireworks-shared/fireworks-settings-2.json')
    .then(response => response.json())
    .then(settings => {
      const fireworkOn = _readFireworkOnSetting();
      if (fireworkOn === false) settings.ENABLED = false;
      else if (fireworkOn === true) settings.ENABLED = true;

      applySettings(settings);
      initializeFireworks();
    })
    .catch(err => {
      SETTINGS = getDefaultSettings();

      const fireworkOn = _readFireworkOnSetting();
      if (fireworkOn === false) SETTINGS.ENABLED = false;
      else if (fireworkOn === true) SETTINGS.ENABLED = true;

      initializeFireworks();
    });
}

function applySettings(settings) {
  SETTINGS = { ...getDefaultSettings(), ...settings };
  const base = _getAssetBasePath();
  SETTINGS.TEXTURES = { SPARKLE: `${base}/sparkle.png`, FIREBALL: `${base}/fireball.png` };
  isEnabled = SETTINGS.ENABLED !== false;
}

function initializeFireworks() {
  createFallbackTextures();

  new THREE.TextureLoader().load(
    SETTINGS.TEXTURES.SPARKLE,
    (tex) => {
      sparkleTexture = tex;
      _rebuildMaterialTemplates();
    },
    undefined,
    () => {}
  );

  new THREE.TextureLoader().load(
    SETTINGS.TEXTURES.FIREBALL,
    (tex) => {
      fireballTexture = tex;
      _rebuildMaterialTemplates();
    },
    undefined,
    () => {}
  );

  _rebuildMaterialTemplates();
  _cachedZeroPlane = document.querySelector('#stock-3d-cube');

  if (_isVRMode()) {
    if (!AFRAME.components['fireworks-tick']) {
      AFRAME.registerComponent('fireworks-tick', {
        tick: function () { _onSceneTick(); }
      });
    }
    if (sceneEl && !sceneEl.components['fireworks-tick']) {
      sceneEl.setAttribute('fireworks-tick', '');
    }
  }

  if (!isEnabled) return;
  if (SETTINGS.AUTO_LAUNCH?.ENABLED) startAutoLaunch();
}

function _rebuildMaterialTemplates() {
  const vrMode = _isVRMode();
  const blending = vrMode ? THREE.NormalBlending : THREE.AdditiveBlending;
  const particleSizeMultiplier = vrMode ? 2.0 : 1.5;

  if (_rocketMaterialTemplate) _rocketMaterialTemplate.dispose();
  _rocketMaterialTemplate = new THREE.SpriteMaterial({
    map: fireballTexture,
    transparent: true,
    depthWrite: false,
    blending: blending,
    alphaTest: 0.01
  });

  if (_explosionMaterialTemplate) _explosionMaterialTemplate.dispose();
  _explosionMaterialTemplate = new THREE.PointsMaterial({
    size: (SETTINGS.EXPLOSION.PARTICLE_SIZE || 0.03) * particleSizeMultiplier,
    map: sparkleTexture,
    vertexColors: true,
    transparent: true,
    opacity: 1.0,
    alphaTest: 0.01,
    depthWrite: false,
    blending: blending,
    sizeAttenuation: true
  });
}

function createFallbackTextures() {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.2, 'rgba(255,255,200,0.8)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 64, 64);
  sparkleTexture = new THREE.CanvasTexture(canvas);
  fireballTexture = sparkleTexture;
}

const VR_POOL_MAX_PARTICLES = 192;
let _vrParticlePool = null;
const _tempColor = new THREE.Color();

class VRParticlePool {
  constructor() {
    this.maxParticles = VR_POOL_MAX_PARTICLES;
    this.lifetimes = new Float32Array(this.maxParticles);
    this.maxLifetimes = new Float32Array(this.maxParticles);
    this.velocities = new Float32Array(this.maxParticles * 3);
    this.origR = new Float32Array(this.maxParticles);
    this.origG = new Float32Array(this.maxParticles);
    this.origB = new Float32Array(this.maxParticles);
    this.active = new Uint8Array(this.maxParticles);
    this.explosionId = new Int16Array(this.maxParticles).fill(-1);
    this._nextExplosionId = 0;
    this._activeExplosionIds = new Set();

    const positions = new Float32Array(this.maxParticles * 3);
    const colors = new Float32Array(this.maxParticles * 3);
    for (let i = 0; i < this.maxParticles; i++) {
      positions[i * 3 + 1] = -1000; // off-screen
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    this.material = new THREE.PointsMaterial({
      size: (SETTINGS.EXPLOSION.PARTICLE_SIZE || 0.03) * 2.0,
      map: sparkleTexture,
      vertexColors: true,
      transparent: true,
      opacity: 1.0,
      alphaTest: 0.01,
      depthWrite: false,
      blending: THREE.NormalBlending,
      sizeAttenuation: true
    });

    this.mesh = new THREE.Points(this.geometry, this.material);
    this.mesh.renderOrder = 99999;
    this.mesh.frustumCulled = false;
    this.mesh.userData._isFireworksPool = true;

    const zeroPlane = _getZeroPlane();
    this._parent = (zeroPlane && zeroPlane.object3D) ? zeroPlane.object3D : sceneEl.object3D;
    this._parent.add(this.mesh);
  }

  addExplosion(x, y, z, r, g, b) {
    const count = 12;
    const maxLife = SETTINGS.EXPLOSION.LIFETIME || 0.8;
    const positions = this.geometry.attributes.position.array;
    const colors = this.geometry.attributes.color.array;
    const velMin = SETTINGS.EXPLOSION.VELOCITY.min;
    const velRange = SETTINGS.EXPLOSION.VELOCITY.max - velMin;
    const eid = this._nextExplosionId++;
    let added = 0;

    for (let i = 0; i < this.maxParticles && added < count; i++) {
      if (this.active[i]) continue;

      this.active[i] = 1;
      this.lifetimes[i] = 0;
      this.maxLifetimes[i] = maxLife;
      this.explosionId[i] = eid;

      const idx = i * 3;
      positions[idx] = x;
      positions[idx + 1] = y;
      positions[idx + 2] = z;

      const theta = Math.random() * 6.283185307;
      const phi = Math.acos(2 * Math.random() - 1);
      const speed = Math.random() * velRange + velMin;
      const sinPhi = Math.sin(phi);
      this.velocities[idx] = speed * sinPhi * Math.cos(theta);
      this.velocities[idx + 1] = speed * Math.cos(phi) * 0.4;
      this.velocities[idx + 2] = speed * sinPhi * Math.sin(theta);

      colors[idx] = r; colors[idx + 1] = g; colors[idx + 2] = b;
      this.origR[i] = r; this.origG[i] = g; this.origB[i] = b;

      added++;
    }

    if (added > 0) {
      this._activeExplosionIds.add(eid);
      _activeFireworkCount++;
      this.geometry.attributes.position.needsUpdate = true;
      this.geometry.attributes.color.needsUpdate = true;
    }
    return added > 0;
  }

  step(delta) {
    if (this._activeExplosionIds.size === 0) return;

    const positions = this.geometry.attributes.position.array;
    const colors = this.geometry.attributes.color.array;
    const gravity = SETTINGS.PHYSICS.GRAVITY || -0.5;
    const midpoint = SETTINGS.EXPLOSION.COLOR_TRANSITION?.midpoint || 0.3;
    const completedEids = new Set();

    for (let i = 0; i < this.maxParticles; i++) {
      if (!this.active[i]) continue;

      this.lifetimes[i] += delta;
      const progress = this.lifetimes[i] / this.maxLifetimes[i];

      if (progress >= 1.0) {
        this.active[i] = 0;
        const idx = i * 3;
        positions[idx + 1] = -1000;
        colors[idx] = 0; colors[idx + 1] = 0; colors[idx + 2] = 0;
        completedEids.add(this.explosionId[i]);
        this.explosionId[i] = -1;
        continue;
      }

      const idx = i * 3;
      this.velocities[idx + 1] += gravity * delta;
      positions[idx] += this.velocities[idx] * delta;
      positions[idx + 1] += this.velocities[idx + 1] * delta;
      positions[idx + 2] += this.velocities[idx + 2] * delta;
      this.velocities[idx] *= 0.98;
      this.velocities[idx + 2] *= 0.98;

      // Fade via color dimming (equivalent to opacity with AdditiveBlending)
      if (progress > midpoint) {
        const fade = Math.max(0, 1 - (progress - midpoint) / (1 - midpoint));
        colors[idx] = this.origR[i] * fade;
        colors[idx + 1] = this.origG[i] * fade;
        colors[idx + 2] = this.origB[i] * fade;
      }
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;

    for (const eid of completedEids) {
      let stillActive = false;
      for (let j = 0; j < this.maxParticles; j++) {
        if (this.explosionId[j] === eid) { stillActive = true; break; }
      }
      if (!stillActive && this._activeExplosionIds.delete(eid)) {
        _activeFireworkCount = Math.max(0, _activeFireworkCount - 1);
      }
    }
  }

  clear() {
    for (let i = 0; i < this.maxParticles; i++) {
      this.active[i] = 0;
      this.explosionId[i] = -1;
      const idx = i * 3;
      this.geometry.attributes.position.array[idx + 1] = -1000;
      this.geometry.attributes.color.array[idx] = 0;
      this.geometry.attributes.color.array[idx + 1] = 0;
      this.geometry.attributes.color.array[idx + 2] = 0;
    }
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;
    this._activeExplosionIds.clear();
  }

  dispose() {
    if (this._parent) this._parent.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
    _vrParticlePool = null;
  }
}

function _getVRParticlePool() {
  if (!_vrParticlePool && sceneEl) {
    _vrParticlePool = new VRParticlePool();
  }
  return _vrParticlePool;
}

class Rocket {
  constructor(x, y, z, colorHex, direction = null, travelHeight = null, anchor = null) {
    if (!isEnabled) return;

    _activeFireworkCount++;

    this.anchor = anchor;
    this.pos = new THREE.Vector3(x, y, z);
    this.color = new THREE.Color(colorHex);

    const gravity = Math.abs(SETTINGS.PHYSICS.GRAVITY || -0.5);
    const vrMode = _isVRMode();
    const vrSpeedMult = vrMode ? 3.0 : 1.0;

    let velY =
      Math.random() * (SETTINGS.ROCKET.VELOCITY.max - SETTINGS.ROCKET.VELOCITY.min) +
      SETTINGS.ROCKET.VELOCITY.min;

    if (travelHeight !== null && travelHeight !== undefined) {
      velY = Math.sqrt(2 * gravity * travelHeight);
    }

    velY *= vrSpeedMult;

    this.vel = new THREE.Vector3(
      direction ? direction.x * velY : (Math.random() - 0.5) * SETTINGS.ROCKET.LAUNCH_AREA.x.max,
      velY,
      direction ? direction.z * velY : (Math.random() - 0.5) * SETTINGS.ROCKET.LAUNCH_AREA.z.max
    );

    this.maxHeight = (travelHeight !== null && travelHeight !== undefined)
      ? y + travelHeight
      : y + Math.random() * (SETTINGS.ROCKET.HEIGHT.max - SETTINGS.ROCKET.HEIGHT.min) + SETTINGS.ROCKET.HEIGHT.min;

    this.createMesh();
    _activeRockets.add(this);
    _scheduleTick();
  }

  createMesh() {
    const material = _rocketMaterialTemplate.clone();
    material.color = this.color;

    this.sprite = new THREE.Sprite(material);
    const renderSize = SETTINGS.ROCKET.SIZE * 1.5;
    this.sprite.scale.set(renderSize, renderSize, renderSize);
    this.sprite.renderOrder = 99999;
    this.sprite.position.copy(this.pos);

    if (this.anchor && this.anchor.object3D && this.anchor.object3D.parent) {
      this._parent = this.anchor.object3D.parent;
    } else {
      const zeroPlane = _getZeroPlane();
      this._parent = (zeroPlane && zeroPlane.object3D) ? zeroPlane.object3D : sceneEl.object3D;
    }
    this._parent.add(this.sprite);
  }

  step(delta) {
    this.pos.addScaledVector(this.vel, delta);
    this.vel.y += SETTINGS.PHYSICS.GRAVITY * delta;
    this.sprite.position.copy(this.pos);

    if (this.pos.y >= this.maxHeight || this.vel.y <= 0) {
      this.explode();
    }
  }

  explode() {
    _activeRockets.delete(this);
    if (this._parent) this._parent.remove(this.sprite);
    this.sprite.material.dispose();
    new Explosion(this.pos, this.color, this.anchor);
  }
}

class Explosion {
  constructor(position, color, anchor = null) {
    if (!isEnabled) return;
    this.position = position.clone();
    this.baseColor = color;
    this.anchor = anchor;
    this.life = 0;
    this.maxLife = SETTINGS.EXPLOSION.LIFETIME || 0.8;
    this.particleCount = _isVRMode() ? 12 : (SETTINGS.EXPLOSION.PARTICLE_COUNT || 25);
    this.init();
    _activeExplosions.add(this);
    _scheduleTick();
  }

  init() {
    const particleCount = this.particleCount;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      const theta = Math.random() * 2 * Math.PI;
      const phi = Math.acos(2 * Math.random() - 1);
      const speed = Math.random() * (SETTINGS.EXPLOSION.VELOCITY.max - SETTINGS.EXPLOSION.VELOCITY.min) + SETTINGS.EXPLOSION.VELOCITY.min;
      const vx = speed * Math.sin(phi) * Math.cos(theta);
      const vy = speed * Math.cos(phi) * 0.4;
      const vz = speed * Math.sin(phi) * Math.sin(theta);
      velocities[i * 3] = vx;
      velocities[i * 3 + 1] = vy;
      velocities[i * 3 + 2] = vz;
      positions[i * 3] = this.position.x;
      positions[i * 3 + 1] = this.position.y;
      positions[i * 3 + 2] = this.position.z;
      colors[i * 3] = this.baseColor.r;
      colors[i * 3 + 1] = this.baseColor.g;
      colors[i * 3 + 2] = this.baseColor.b;
    }

    this.velocities = velocities;
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    this.material = _explosionMaterialTemplate.clone();

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.renderOrder = 99999;

    if (this.anchor && this.anchor.object3D && this.anchor.object3D.parent) {
      this._parent = this.anchor.object3D.parent;
    } else {
      const zeroPlane = _getZeroPlane();
      this._parent = (zeroPlane && zeroPlane.object3D) ? zeroPlane.object3D : sceneEl.object3D;
    }
    this._parent.add(this.points);
  }

  step(delta) {
    this.life += delta;
    const positions = this.geometry.attributes.position.array;
    const progress = this.life / this.maxLife;

    for (let i = 0; i < this.particleCount; i++) {
      const idx = i * 3;
      this.velocities[idx + 1] += SETTINGS.PHYSICS.GRAVITY * delta;
      positions[idx] += this.velocities[idx] * delta;
      positions[idx + 1] += this.velocities[idx + 1] * delta;
      positions[idx + 2] += this.velocities[idx + 2] * delta;
      this.velocities[idx] *= 0.98;
      this.velocities[idx + 2] *= 0.98;
    }

    this.geometry.attributes.position.needsUpdate = true;
    const midpoint = SETTINGS.EXPLOSION.COLOR_TRANSITION?.midpoint || 0.3;
    if (progress > midpoint) {
      const fadeProgress = (progress - midpoint) / (1 - midpoint);
      this.material.opacity = Math.max(0, 1 - fadeProgress);
    }

    if (this.life >= this.maxLife) {
      this.dispose();
    }
  }

  dispose() {
    _activeExplosions.delete(this);
    if (this._parent) this._parent.remove(this.points);
    this.geometry.dispose();
    this.material.dispose();
    _activeFireworkCount = Math.max(0, _activeFireworkCount - 1);
  }
}

// ─── Public launch functions ───

function launchFireworkAtPosition(x, y, z, direction = null, color = null, travelHeight = null, anchor = null) {
  if (!isEnabled) return;

  const maxConcurrent = SETTINGS.PERFORMANCE?.MAX_CONCURRENT_FIREWORKS || 50;
  if (_activeFireworkCount >= maxConcurrent) return;

  const targetColor = color || SETTINGS.COLORS.PALETTE[Math.floor(Math.random() * SETTINGS.COLORS.PALETTE.length)];
  new Rocket(x, y, z, targetColor, direction, travelHeight, anchor);
}

function launchFireworkBurstAtPosition(barInfo, x, y, z, direction = null, color = null, duration = 5000, interval = 500) {
  if (!isEnabled) return;
  const vrMode = _isVRMode();
  const effectiveDuration = vrMode ? 5000 : duration;
  const effectiveInterval = vrMode ? 600 : interval;
  const startTime = performance.now();
  const travelHeight = barInfo ? barInfo.fireworkTravelHeight : null;

  const launchLoop = () => {
    const now = performance.now();
    if (now - startTime < effectiveDuration) {
      const colorAtLaunch = color || SETTINGS.COLORS.PALETTE[Math.floor(Math.random() * SETTINGS.COLORS.PALETTE.length)];
      const anchor = barInfo?.bar;
      launchFireworkAtPosition(x, y, z, direction, colorAtLaunch, travelHeight, anchor);
      _trackTimeout(launchLoop, effectiveInterval);
    }
  };

  launchLoop();
}

function launchRandomFirework() {
  if (!isEnabled) return;
  const x = (Math.random() - 0.5) * (SETTINGS.ROCKET.LAUNCH_AREA.x.max - SETTINGS.ROCKET.LAUNCH_AREA.x.min);
  const y = 0.5 + Math.random() * 0.5;
  const z = -1.5 + (Math.random() - 0.5) * 1.0;
  const color = SETTINGS.COLORS.PALETTE[Math.floor(Math.random() * SETTINGS.COLORS.PALETTE.length)];
  new Rocket(x, y, z, color);
}

function launchFireworksGroup(count = null) {
  if (!isEnabled) return;
  const n = count || Math.floor(
    Math.random() * (SETTINGS.AUTO_LAUNCH.MAX_GROUP_SIZE - SETTINGS.AUTO_LAUNCH.MIN_GROUP_SIZE + 1) +
    SETTINGS.AUTO_LAUNCH.MIN_GROUP_SIZE
  );
  for (let i = 0; i < n; i++) {
    _trackTimeout(() => launchRandomFirework(), i * 150);
  }
}

function startAutoLaunch() {
  stopAutoLaunch();
  if (!isEnabled || !SETTINGS.AUTO_LAUNCH?.ENABLED) return;
  _autoLaunchIntervalId = setInterval(() => {
    launchFireworksGroup();
  }, SETTINGS.AUTO_LAUNCH.INTERVAL || 5000);
}

function stopAutoLaunch() {
  if (_autoLaunchIntervalId) {
    clearInterval(_autoLaunchIntervalId);
    _autoLaunchIntervalId = null;
  }
}

function emergencyStopFireworks() {
  stopAutoLaunch();

  for (const rocket of _activeRockets) {
    if (rocket._parent) rocket._parent.remove(rocket.sprite);
    rocket.sprite.material.dispose();
  }
  _activeRockets.clear();

  for (const explosion of _activeExplosions) {
    if (explosion._parent) explosion._parent.remove(explosion.points);
    explosion.geometry.dispose();
    explosion.material.dispose();
  }
  _activeExplosions.clear();

  _tickScheduled = false;

  const zeroPlane = _getZeroPlane();
  const scene = sceneEl?.object3D;

  function removeFireworksObjects(object) {
    if (!object) return;

    const isRocket = object instanceof THREE.Sprite && object.material && object.material.map === fireballTexture;
    const isExplosion = object instanceof THREE.Points && object.material && object.material.map === sparkleTexture;

    if (isRocket || isExplosion) {
      if (object.parent) object.parent.remove(object);
      if (object.geometry) object.geometry.dispose();
      if (object.material) {
        object.material.dispose();
      }
      return;
    }

    if (object.children) {
      for (let i = object.children.length - 1; i >= 0; i--) {
        removeFireworksObjects(object.children[i]);
      }
    }
  }

  if (zeroPlane && zeroPlane.object3D) removeFireworksObjects(zeroPlane.object3D);
  if (scene) removeFireworksObjects(scene);

  const xrSession = _getXRSession();
  for (const id of _activeTimeoutIds) {
    clearTimeout(id);
  }
  _activeTimeoutIds.clear();
  _activeFireworkCount = 0;
}

function updateSettings(newSettings) {
  fetch('/js/fireworks-shared/fireworks-settings-2.json')
    .then(response => response.json())
    .then(currentSettings => {
      applySettings({ ...currentSettings, ...newSettings });
    })
    .catch(() => {});
}

function toggleFireworks(enabled) {
  isEnabled = enabled;
  if (!isEnabled) stopAutoLaunch();
}

function blinkElement(element, color, duration = 5000) {
  if (!element) return;
  const originalColor = '#fff';
  element.setAttribute('animation__blink', {
    property: 'color',
    from: originalColor,
    to: color,
    dur: 1000,
    dir: 'alternate',
    loop: true,
    easing: 'easeInOutQuad'
  });
  setTimeout(() => {
    element.removeAttribute('animation__blink');
    element.setAttribute('color', originalColor);
  }, duration);
}

// ─── Init ───

function onLoad() {
  sceneEl = document.querySelector('a-scene');
  if (sceneEl) {
    if (sceneEl.hasLoaded) {
      loadSettings();
    } else {
      sceneEl.addEventListener('loaded', () => loadSettings());
    }
  } else {
    setTimeout(onLoad, 100);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', onLoad);
} else {
  onLoad();
}

window.Fireworks = {
  launchFireworkAtPosition,
  launchFireworkBurstAtPosition,
  launchRandomFirework,
  launchFireworksGroup,
  startAutoLaunch,
  stopAutoLaunch,
  emergencyStopFireworks,
  updateSettings,
  toggleFireworks,
  blinkElement,
  getSettings: () => SETTINGS,
  invalidateZeroPlaneCache: () => { _cachedZeroPlane = null; }
};
